import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { LoaderCircle, MapPin, TriangleAlert } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { t } from "@/lib/i18n"

type IdentityPayload = {
  token: string
  locationName: string
  tableName: string | null
  maxNameLength: number
  error: string | null
}

type Props = {
  identity: IdentityPayload
}

// The last step before a seat. The diner is already past whatever door this table
// has — a waiter's QR, the geofence, someone's approval — so nothing here decides
// whether they get in. It decides what their table-mates will read on the bill.
export default function UserInvitesIdentify() {
  const { identity } = usePage<Props>().props
  const [name, setName] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const join = () => {
    if (name.trim().length === 0) return

    setSubmitting(true)
    router.post(
      `/invites/${identity.token}/join`,
      { name },
      { onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <UserLayout locationName={identity.locationName}>
      <form
        data-testid="invite-identify-view"
        className="mx-auto flex w-full max-w-md flex-col gap-6 px-4 py-10"
        onSubmit={(event) => {
          event.preventDefault()
          join()
        }}
      >
        <header className="space-y-2 text-center">
          {identity.tableName && (
            <span className="mx-auto inline-flex items-center gap-1.5 rounded-full bg-brand-subtle px-2.5 py-1 text-xs font-semibold text-brand-ink">
              <MapPin className="size-3.5" />
              {t("ui.user.common.table_label", { name: identity.tableName })}
            </span>
          )}
          <h1 className="text-xl font-semibold tracking-tight">
            {t("ui.react.pages.user.invites.identify.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("ui.react.pages.user.invites.identify.explanation")}
          </p>
        </header>

        {identity.error && (
          <p
            data-testid="invite-identify-error"
            className="flex items-center gap-2 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
          >
            <TriangleAlert className="size-4 shrink-0" />
            {identity.error}
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="diner-name">
            {t("ui.react.pages.user.invites.identify.label")}
          </Label>
          <Input
            id="diner-name"
            name="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={identity.maxNameLength}
            autoComplete="given-name"
            autoFocus
            placeholder={t("ui.react.pages.user.invites.identify.placeholder")}
            data-testid="invite-identify-name"
          />
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={name.trim().length === 0 || submitting}
          data-testid="invite-identify-submit"
        >
          {submitting && <LoaderCircle className="animate-spin" />}
          {t("ui.react.pages.user.invites.identify.action")}
        </Button>
      </form>
    </UserLayout>
  )
}
